import { useI18n } from "@/locales/client";
import { useKForm } from "@/common/use-k-form";
import {
    type CredentialsSignInDto,
    credentialsSignInSchema,
} from "@/modules/authentication/schemas/credentials-sign-in-schema";
import { useCsmCompaniesQuery } from "@/modules/authentication/queries/use-csm-companies-query";
import { useSearchParams } from "next/navigation";
import { KInput } from "@/components/k-input";
import { KIcon } from "@/components/k-icon";
import { Apartment, LockRounded, Person } from "@mui/icons-material";
import { KOption, KSelect } from "@/components/k-select";
import { KLoadingView } from "@/components/k-loading-view";
import { KButton } from "@/components/k-button";
import { useCredentialsLogin } from "@/modules/authentication/mutations/authentication-mutations";

export const CredentialSignIn = ({ provider }: { provider: string }) => {
    const t = useI18n();
    const { control, handleSubmit, formState } = useKForm({
        schema: credentialsSignInSchema,
        defaultValues: {
            username: "",
            password: "",
            company: "",
        },
    });

    const {
        data: companies,
        isPending: isCompaniesPending,
        error: companiesError,
    } = useCsmCompaniesQuery();

    const { mutate: signIn, isPending: loginInProgress } =
        useCredentialsLogin();

    const onSubmit = async (credentials: CredentialsSignInDto) => {
        void signIn({ provider, credentials });
    };

    const params = useSearchParams();
    const error = params?.get("error") ?? undefined;
    const loginFailed = error !== undefined && error !== "";

    return (
        <>
            {loginFailed && (
                <div className="mb-4 rounded bg-yellow-500 p-4 text-white">
                    {t("InvalidCredentials")}
                </div>
            )}
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex min-w-[40vw] flex-col gap-6"
            >
                <KInput
                    control={control}
                    placeholder={t("Username")}
                    name="username"
                    className="flex-1"
                    prefix={<KIcon icon={<Person />} />}
                />
                <KInput
                    control={control}
                    placeholder={t("Password")}
                    name="password"
                    type="password"
                    prefix={<KIcon icon={<LockRounded />} />}
                />
                {companies ? (
                    <KSelect
                        control={control}
                        name="company"
                        placeholder={t("Company")}
                        prefix={<KIcon icon={<Apartment />} />}
                    >
                        {companies?.map((value) => {
                            return (
                                <KOption key={value.id} value={value.id}>
                                    {value.name}
                                </KOption>
                            );
                        })}
                    </KSelect>
                ) : (
                    <>
                        {isCompaniesPending && <KLoadingView />}
                        {companiesError && (
                            <pre>{JSON.stringify(error, null, 2)}</pre>
                        )}
                    </>
                )}

                <div className="flex w-full justify-center">
                    <KButton
                        className="h-13 min-w-[200px]"
                        disabled={!formState.isValid || loginInProgress}
                    >
                        {t("SignIn")}
                    </KButton>
                </div>
            </form>
        </>
    );
};
